import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Save, ArrowLeft, AlertCircle, CheckCircle } from "lucide-react";
import { ADMIN, STORAGE_KEYS, ASSETS } from "../../lib/constants";
import { CONNECTION_GAMES } from "../../lib/data";
import { fetchAdminPassword, updatePuzzleData } from "../../lib/firebase";

// VIBRANT difficulty colors
const DIFFICULTY_OPTIONS = [
    { value: 1, label: "Yellow (Straightforward)", color: "#FDE047" },
    { value: 2, label: "Green (Medium)", color: "#4ADE80" },
    { value: 3, label: "Blue (Hard)", color: "#60A5FA" },
    { value: 4, label: "Purple (Tricky)", color: "#C084FC" },
];

// Default empty category
const createEmptyCategory = () => ({
    category: "",
    words: ["", "", "", ""],
    difficulty: 1,
});

// Default empty puzzle
const createEmptyPuzzle = () => [
    createEmptyCategory(),
    createEmptyCategory(),
    createEmptyCategory(),
    createEmptyCategory(),
];

function PasswordGate({ onAuthenticate }) {
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoadingPassword, setIsLoadingPassword] = useState(true);
    const [firebasePassword, setFirebasePassword] = useState(null);

    // Load Firebase password on mount
    useEffect(() => {
        const loadPassword = async () => {
            try {
                const pwd = await fetchAdminPassword();
                setFirebasePassword(pwd || ADMIN.password); // Fallback to hardcoded password
            } catch (e) {
                console.error('Failed to load admin password:', e);
                setFirebasePassword(ADMIN.password); // Fallback to hardcoded password
            } finally {
                setIsLoadingPassword(false);
            }
        };
        loadPassword();
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!firebasePassword) {
            setError("Unable to verify password. Please try again.");
            return;
        }

        if (password === firebasePassword) {
            onAuthenticate(true);
            sessionStorage.setItem("admin_auth", firebasePassword);
        } else {
            setError("Incorrect password");
            setPassword("");
        }
    };

    // Show loading state while fetching password
    if (isLoadingPassword) {
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#38B6FF] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
            <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-xl">
                <div className="flex flex-col items-center gap-4 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100">
                        <Lock className="h-6 w-6 text-slate-600" />
                    </div>
                    <div className="space-y-1">
                        <h1 className="text-2xl font-bold text-slate-900">Admin Access</h1>
                        <p className="text-sm text-slate-500">Enter the admin password to continue</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Password"
                        autoComplete="current-password"
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-base text-slate-800 focus:border-[#38B6FF] focus:outline-none focus:ring-2 focus:ring-[#38B6FF]/50"
                        autoFocus
                    />
                    {error && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        className="w-full rounded-full bg-gradient-to-r from-slate-900 to-slate-800 py-3 font-semibold text-white shadow-md transition-all hover:shadow-lg"
                    >
                        Access Admin
                    </button>
                </form>

                <Link
                    to="/"
                    className="mt-5 flex w-full items-center justify-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-700"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Game
                </Link>

                <div className="mt-6 flex justify-center">
                    <img
                        src={ASSETS.occGamesLogo}
                        alt="Roslyn OCC Games"
                        className="h-10 w-auto object-contain opacity-80"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

function CategoryEditor({ category, index, onChange, onRemove }) {
    const handleCategoryChange = (field, value) => {
        onChange(index, { ...category, [field]: value });
    };

    const handleWordChange = (wordIndex, value) => {
        const newWords = [...category.words];
        newWords[wordIndex] = value;
        onChange(index, { ...category, words: newWords });
    };

    const difficultyColor = DIFFICULTY_OPTIONS.find(d => d.value === category.difficulty)?.color || "#f9df6d";

    return (
        <div
            className="border border-gray-200 rounded-xl p-4 space-y-4"
            style={{ borderLeftWidth: "4px", borderLeftColor: difficultyColor }}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category Name
                    </label>
                    <input
                        type="text"
                        value={category.category}
                        onChange={(e) => handleCategoryChange("category", e.target.value)}
                        placeholder="e.g., Types of Coffee"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    />
                </div>
                <div className="w-48">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Difficulty
                    </label>
                    <select
                        value={category.difficulty}
                        onChange={(e) => handleCategoryChange("difficulty", parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white"
                    >
                        {DIFFICULTY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Words (4 required)
                </label>
                <div className="grid grid-cols-2 gap-2">
                    {category.words.map((word, wordIndex) => (
                        <input
                            key={wordIndex}
                            type="text"
                            value={word}
                            onChange={(e) => handleWordChange(wordIndex, e.target.value)}
                            placeholder={`Word ${wordIndex + 1}`}
                            className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

function PuzzleEditor() {
    const navigate = useNavigate();
    const [puzzle, setPuzzle] = useState(() => {
        // Try to load from localStorage first, then fall back to default
        try {
            const stored = localStorage.getItem(STORAGE_KEYS.adminPuzzle);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (e) {
            console.error("Error loading puzzle:", e);
        }
        // Fall back to the first puzzle in CONNECTION_GAMES
        return CONNECTION_GAMES[0] || createEmptyPuzzle();
    });

    const [saveStatus, setSaveStatus] = useState(null);
    const [isLocked, setIsLocked] = useState(() => {
        try {
            const stored = localStorage.getItem('clashconnections_isLocked');
            return stored ? JSON.parse(stored) : false;
        } catch (e) {
            return false;
        }
    });

    const handleLogout = () => {
        sessionStorage.removeItem("admin_auth");
        navigate("/");
    };

    // Validation
    const validatePuzzle = () => {
        const errors = [];

        // Check we have exactly 4 categories
        if (puzzle.length !== 4) {
            errors.push("Puzzle must have exactly 4 categories");
        }

        // Check each category
        puzzle.forEach((cat, index) => {
            if (!cat.category.trim()) {
                errors.push(`Category ${index + 1} needs a name`);
            }
            cat.words.forEach((word, wordIndex) => {
                if (!word.trim()) {
                    errors.push(`Category ${index + 1}, Word ${wordIndex + 1} is empty`);
                }
            });
        });

        // Check for duplicate words
        const allWords = puzzle.flatMap(cat => cat.words.map(w => w.toLowerCase().trim()));
        const uniqueWords = new Set(allWords);
        if (uniqueWords.size !== allWords.length) {
            errors.push("All 16 words must be unique");
        }

        return errors;
    };

    const handleCategoryChange = (index, updatedCategory) => {
        const newPuzzle = [...puzzle];
        newPuzzle[index] = updatedCategory;
        setPuzzle(newPuzzle);
        setSaveStatus(null);
    };

    const handleSave = async () => {
        const errors = validatePuzzle();
        if (errors.length > 0) {
            setSaveStatus({ type: "error", messages: errors });
            return;
        }

        try {
            // Save to Firebase first
            await updatePuzzleData(puzzle);

            // Also save to localStorage as backup
            localStorage.setItem(STORAGE_KEYS.adminPuzzle, JSON.stringify(puzzle));

            setSaveStatus({ type: "success", messages: ["Puzzle saved successfully! Players will see this puzzle."] });
        } catch (e) {
            console.error("Failed to save puzzle:", e);
            setSaveStatus({ type: "error", messages: ["Failed to save puzzle. " + (e.message || "Please try again.")] });
        }
    };

    const handleReset = () => {
        if (window.confirm("Reset to the default puzzle? This will discard your changes.")) {
            setPuzzle(CONNECTION_GAMES[0] || createEmptyPuzzle());
            localStorage.removeItem(STORAGE_KEYS.adminPuzzle);
            setSaveStatus({ type: "success", messages: ["Reset to default puzzle"] });
        }
    };

    const handleClear = () => {
        if (window.confirm("Clear all fields? This will create a blank puzzle.")) {
            setPuzzle(createEmptyPuzzle());
            setSaveStatus(null);
        }
    };

    const handleToggleLockdown = async () => {
        try {
            const newLockState = !isLocked;
            await updatePuzzleData(puzzle, newLockState);
            setIsLocked(newLockState);
            setSaveStatus({
                type: "success",
                messages: [newLockState ? "Game locked - lockdown screen enabled" : "Game unlocked - players can access"]
            });
        } catch (e) {
            console.error("Failed to toggle lockdown:", e);
            setSaveStatus({ type: "error", messages: ["Failed to toggle lockdown. " + (e.message || "Please try again.")] });
        }
    };

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Admin Header - Clash Style */}
            <header className="clash-header">
                <div className="header-left">
                    <img src={ASSETS.clashLogo} alt="CLASH" className="header-logo" />
                </div>
                <div className="header-center">
                    <span className="header-title">Connections Manager</span>
                </div>
                <div className="header-right">
                    <button
                        onClick={handleLogout}
                        className="header-btn px-4"
                        style={{ width: 'auto', borderRadius: '26px' }}
                    >
                        Logout
                    </button>
                </div>
            </header>

            {/* Secondary toolbar */}
            <div className="bg-white border-b border-gray-200 sticky top-[72px] z-10">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
                    <p className="text-sm text-gray-500">Puzzle Editor</p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleReset}
                            className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
                        >
                            Reset to Default
                        </button>
                        <button
                            onClick={handleClear}
                            className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium text-sm"
                        >
                            Clear All
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-6 py-2 bg-[#38B6FF] text-white rounded-full font-medium hover:bg-[#1E9FE8] transition-colors flex items-center gap-2"
                        >
                            <Save className="w-4 h-4" />
                            Save Puzzle
                        </button>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <main className="max-w-4xl mx-auto px-4 py-8">
                {/* Status messages */}
                {saveStatus && (
                    <div
                        className={`mb-6 p-4 rounded-xl flex items-start gap-3 ${saveStatus.type === "error"
                                ? "bg-red-50 border border-red-200"
                                : "bg-green-50 border border-green-200"
                            }`}
                    >
                        {saveStatus.type === "error" ? (
                            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        ) : (
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                            {saveStatus.messages.map((msg, i) => (
                                <p
                                    key={i}
                                    className={`text-sm ${saveStatus.type === "error" ? "text-red-700" : "text-green-700"
                                        }`}
                                >
                                    {msg}
                                </p>
                            ))}
                        </div>
                    </div>
                )}

                {/* Lockdown Toggle */}
                <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200 mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Game Lockdown</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {isLocked
                                    ? 'Game is currently locked - players see lockdown screen'
                                    : 'Game is accessible - players can play normally'}
                            </p>
                        </div>
                        <button
                            onClick={handleToggleLockdown}
                            className={`px-6 py-3 rounded-full font-bold text-white shadow-md hover:shadow-lg transition-all ${isLocked
                                    ? 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600'
                                    : 'bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600'
                                }`}
                        >
                            {isLocked ? 'Unlock Game' : 'Lock Game'}
                        </button>
                    </div>
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                    <h2 className="font-medium text-blue-900 mb-2">How to create a puzzle:</h2>
                    <ul className="text-sm text-blue-800 space-y-1">
                        <li>1. Create 4 categories, each with 4 words that share a common theme</li>
                        <li>2. Set the difficulty for each category (Yellow = easiest, Purple = hardest)</li>
                        <li>3. Make sure all 16 words are unique</li>
                        <li>4. Click "Save Puzzle" to make it live for all players</li>
                    </ul>
                </div>

                {/* Category editors */}
                <div className="space-y-4">
                    {puzzle.map((category, index) => (
                        <CategoryEditor
                            key={index}
                            category={category}
                            index={index}
                            onChange={handleCategoryChange}
                        />
                    ))}
                </div>

                {/* Preview section */}
                <div className="mt-8 p-6 bg-white rounded-xl border border-gray-200">
                    <h2 className="font-bold text-gray-900 mb-4">Preview</h2>
                    <div className="space-y-2">
                        {puzzle.map((cat, index) => {
                            const color = DIFFICULTY_OPTIONS.find(d => d.value === cat.difficulty)?.color;
                            return (
                                <div
                                    key={index}
                                    className="rounded-lg p-3 text-center"
                                    style={{ backgroundColor: color }}
                                >
                                    <p className="font-bold text-sm uppercase tracking-wide" style={{ color: "#333" }}>
                                        {cat.category || "(No category name)"}
                                    </p>
                                    <p className="text-sm mt-1" style={{ color: "#333" }}>
                                        {cat.words.filter(w => w).join(", ") || "(No words)"}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </main>
        </div>
    );
}

function AdminPage() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);

    // Check if already authenticated with Firebase password
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const savedAuth = sessionStorage.getItem("admin_auth");
                if (savedAuth && savedAuth !== "true") {
                    // Verify the saved password is still valid
                    const currentPassword = await fetchAdminPassword();
                    if (savedAuth === currentPassword || savedAuth === ADMIN.password) {
                        setIsAuthenticated(true);
                    }
                }
            } catch (e) {
                console.error("Failed to verify authentication:", e);
            } finally {
                setIsCheckingAuth(false);
            }
        };
        checkAuth();
    }, []);

    if (isCheckingAuth) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <PasswordGate onAuthenticate={setIsAuthenticated} />;
    }

    return <PuzzleEditor />;
}

export default AdminPage;
